import React, { useEffect, useState, useRef, useCallback } from "react"
import { Box, Tile, Typography } from "@carrotfertility/carotene-core"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import { Locale } from "#/types/Locale"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"

declare global {
  interface Window {
    QSI?: any
    Q_Language?: string
  }
}

const JOURNEYS = {
  AssistedReproduction: "ASSISTED_REPRODUCTION",
  TryingToGetPregnant: "TRY_PREGNANT",
  Pregnant: "PREGNANT"
}

const IVF_AND_NON_IVF_OUTCOMES_INTERCEPT = "SI_0Uo9Be9q6hI1kxg"
const TRIMESTER_1_INTERCEPT = "SI_3VqM8qm917bG3Wu"
const TRIMESTER_2_INTERCEPT = "SI_23q1hbLi2rCK2KW"
const TRIMESTER_3_INTERCEPT = "SI_1AhAeCUXhIExaDA"
const TRIMESTER_4_INTERCEPT = "SI_8jGrw2NTmLeGlLg"

// Priority order for intercept IDs (highest priority first)
const INTERCEPT_PRIORITY = [
  IVF_AND_NON_IVF_OUTCOMES_INTERCEPT,
  TRIMESTER_4_INTERCEPT,
  TRIMESTER_3_INTERCEPT,
  TRIMESTER_2_INTERCEPT,
  TRIMESTER_1_INTERCEPT
]

const QualtricsIntercept: React.FC = () => {
  const { employeeId } = useCurrentUser()
  const [isLinkInjected, setIsLinkInjected] = useState(false)
  const linkBoxRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const { locale } = intl

  const memberJourneyQuery = useGetMemberJourneyData()

  const isJourneyDataAvailable = !memberJourneyQuery.isLoading && !memberJourneyQuery.error && memberJourneyQuery.data

  useEffect(() => {
    if (isJourneyDataAvailable && employeeId && window.QSI && locale) {
      window.Q_Language = Locale.getLocaleFromString(locale).qualtricsLanguageCode

      window.QSI.config = {
        ...window.QSI.config,
        externalReference: String(employeeId)
      }

      window.dispatchEvent(new Event("externalReferenceReady"))
    }
  }, [employeeId, locale, isJourneyDataAvailable])

  const getTileText = () => {
    switch (memberJourneyQuery.data?.currentMemberJourney.withSubmittedRouting.journey) {
      case JOURNEYS.AssistedReproduction:
      case JOURNEYS.TryingToGetPregnant:
        return defineMessage({
          defaultMessage:
            "How is your journey to have a baby going so far? Sharing your experience helps us improve our support for everyone."
        })
      case JOURNEYS.Pregnant:
        return defineMessage({
          defaultMessage:
            "How is your pregnancy journey going so far? Sharing your experience helps us improve our support for everyone."
        })
      default:
        return null
    }
  }

  const extractInterceptId = (link: HTMLAnchorElement): string | null => {
    const classes = link.className.split(" ")
    const interceptClass = classes.find((cls) => cls.startsWith("SI_"))
    return interceptClass ? interceptClass.replace("_Link", "") : null
  }

  const applyButtonStyling = (link: HTMLAnchorElement) => {
    Object.assign(link.style, {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      boxSizing: "border-box",
      WebkitTapHighlightColor: "transparent",
      backgroundColor: "transparent",
      outline: "0",
      margin: "0",
      cursor: "pointer",
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      msUserSelect: "none",
      userSelect: "none",
      verticalAlign: "middle",
      MozAppearance: "none",
      WebkitAppearance: "none",
      WebkitTextDecoration: "none",
      textDecoration: "none",
      outlineColor: "#2196F3",
      outlineWidth: "0.125rem",
      outlineOffset: "0.125rem",
      outlineStyle: "none",
      zIndex: "1",
      fontFamily:
        '"Gotham A","Gotham B","Noto Sans","Noto Sans Thai Looped","Noto Sans TC","Noto Sans SC","Noto Sans JP","Noto Sans Arabic","Noto Serif Georgian",sans-serif',
      fontWeight: "600",
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
      textTransform: "unset",
      minWidth: "64px",
      transition:
        "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      border: "1px solid rgba(45, 45, 45, 0.5)",
      color: "#2D2D2D",
      boxShadow: "none",
      padding: "0.5rem 1.5rem",
      borderColor: "#2D2D2D",
      borderWidth: "0.125rem",
      borderStyle: "solid",
      minInlineSize: "96px",
      borderRadius: "50px"
    })

    // Add hover effects
    const handleMouseEnter = () => {
      Object.assign(link.style, {
        backgroundColor: "rgba(45, 45, 45, 0.04)",
        borderColor: "#2D2D2D"
      })
    }

    const handleMouseLeave = () => {
      Object.assign(link.style, {
        backgroundColor: "transparent",
        borderColor: "#2D2D2D"
      })
    }

    // Remove existing event listeners to avoid duplicates
    link.removeEventListener("mouseenter", handleMouseEnter)
    link.removeEventListener("mouseleave", handleMouseLeave)

    // Add event listeners
    link.addEventListener("mouseenter", handleMouseEnter)
    link.addEventListener("mouseleave", handleMouseLeave)
  }

  const prioritizeAndShowSingleLink = useCallback((links: NodeListOf<HTMLAnchorElement>) => {
    // Create array of links with their intercept IDs and priority scores
    const linksWithPriority = Array.from(links)
      .map((link) => {
        const interceptId = extractInterceptId(link)
        const priorityIndex = interceptId ? INTERCEPT_PRIORITY.indexOf(interceptId) : -1

        return {
          link,
          interceptId,
          priority: priorityIndex === -1 ? Infinity : priorityIndex
        }
      })
      .filter((item) => item.interceptId)

    // Sort by priority (lower number = higher priority)
    linksWithPriority.sort((a, b) => a.priority - b.priority)

    // Hide all links first
    links.forEach((link) => {
      link.style.display = "none"
    })

    // Show only the highest priority link
    if (linksWithPriority.length > 0) {
      const highestPriorityLink = linksWithPriority[0].link
      highestPriorityLink.style.display = "inline-flex"
      applyButtonStyling(highestPriorityLink)
      return true
    }

    return false
  }, [])

  useEffect(() => {
    const linkBox = document.getElementById("outcomes-onboarding-survey-link-box")
    if (linkBox) {
      const observer = new MutationObserver(() => {
        const links = linkBox.querySelectorAll<HTMLAnchorElement>("a")
        if (links.length > 0) {
          const hasVisibleLink = prioritizeAndShowSingleLink(links)
          setIsLinkInjected(hasVisibleLink)
          observer.disconnect()
        } else {
          setIsLinkInjected(false)
        }
      })
      observer.observe(linkBox, { childList: true, subtree: true })
      return () => {
        observer.disconnect()
      }
    } else {
      setIsLinkInjected(false)
    }
  }, [memberJourneyQuery.isLoading, memberJourneyQuery.error, memberJourneyQuery.data, prioritizeAndShowSingleLink])

  const tileText = getTileText()

  return (
    <Tile ref={linkBoxRef} sx={isLinkInjected ? {} : { display: "none", visibility: "hidden" }} variant="secondary">
      <Box
        display="flex"
        flexDirection={["column-reverse", "column-reverse", "row"]}
        minWidth="224px"
        gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
        alignItems="center"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
        >
          <Box component="img" src="/images/speech-bubbles.png" alt="" width={93} height={93} />
          <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
            <Box>
              <Typography variant="h5" component="h3">
                <FormattedMessage defaultMessage="We want to hear from you" />
              </Typography>
              {tileText && <Typography>{intl.formatMessage(tileText)}</Typography>}
            </Box>
            <Box id="outcomes-onboarding-survey-link-box"></Box>
          </Box>
        </Box>
      </Box>
    </Tile>
  )
}

export default QualtricsIntercept
